import { Component, inject, signal, effect, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink, RouterLinkActive, NavigationEnd } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs/operators';
import { IMenuItem, MenuItemWithRoles } from 'app/shared/interfaces/IMainLayout';
import { AuthService } from 'app/core/services/auth/auth.service';
import { WORKSPACE_ROLES, KNOWLEDGE_BASE_ROLES, METRICS_ROLES, USER_BASE_ROLES, REQUEST_PANEL_BASE_ROLES } from 'app/core/constants/route-roles.constants';

@Component({
  selector: 'sidebar',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive],
  templateUrl: './sidebar.component.html',
})
export class SidebarComponent {
  private router = inject(Router);
  private auth = inject(AuthService);

  private readonly baseMenuItems: MenuItemWithRoles[] = [
    { label: 'Chats', route: '/veflo-vault/chat', icon: 'chat' },
    { label: 'Noticias', route: '/veflo-vault/home', icon: 'home', isNew: true },
    { label: 'Administración', type: 'separator', roles: WORKSPACE_ROLES },
    { label: 'Usuarios', route: '/veflo-vault/users', icon: 'users', roles: USER_BASE_ROLES },
    { label: 'Espacios de trabajo', route: '/veflo-vault/workspace', icon: 'workspace', roles: WORKSPACE_ROLES },
    { label: 'Base de conocimiento', route: '/veflo-vault/knowledge-base', icon: 'knowledge-base', roles: KNOWLEDGE_BASE_ROLES },
    { label: 'Panel de solicitudes', route: '/veflo-vault/notification/requests-panel', icon: 'panel', roles: REQUEST_PANEL_BASE_ROLES },
    { label: 'Métricas', route: '/veflo-vault/metrics', icon: 'gauge', roles: METRICS_ROLES, isNew: true },
  ];

  readonly menuItems = computed(() => {
    const role = this.auth.userLogin()?.role;
    if (!role) return [];

    return this.baseMenuItems.filter(item => !item.roles || item.roles.includes(role));
  });

  readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(e => e.urlAfterRedirects),
      startWith(this.router.url)
    ),
    { initialValue: this.router.url }
  );

  readonly expanded = signal<Record<string, boolean>>({});

  constructor() {
    effect(() => {
      const url = this.currentUrl();
      for (const item of this.menuItems()) {
        if (item.children?.some(c => url.startsWith(c.route))) {
          this.expanded.update(s => ({ ...s, [item.label]: true }));
        }
      }
    });
  }

  toggle(item: IMenuItem) {
    if (!item.children?.length) return;
    this.expanded.update(s => ({ ...s, [item.label]: !s[item.label] }));
  }

  isExpanded(item: IMenuItem): boolean {
    return !!this.expanded()[item.label];
  }

  groupActive(item: IMenuItem): boolean {
    const url = this.currentUrl();
    return !!item.children?.some(c => url.startsWith(c.route));
  }
}